import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import NumberFormat from 'react-number-format';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Link from '@material-ui/core/Link';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import Loading from 'app/components/loaders/Loading';
import IndustryTextField from 'app/components/textfields/IndustryTextField';
import ChargePaymentMethodForm from 'app/components/accounting/payments/ChargePaymentMethodForm';

import Prefixes from 'app/utils/Prefixes';
import Suffixes from 'app/utils/Suffixes';
import States from 'app/utils/States';
import PasswordPatterns from 'app/utils/PasswordPatterns';

import { img } from "app/styles/core";

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    main: {
        position: 'absolute',
        top: '0px',
        left: '0px',
        height: '100%',
        width: '100%'
    },
    container: {
        zIndex: 12,
        height: '100%',
    },
    loginContainer: {
        padding: theme.spacing(4),

        [theme.breakpoints.down('sm')]: {
            padding: 0
        }
    },
    loginImage: {
        ...img,
        maxWidth: '320px',
        width: 'auto',
        maxHeight: '220px'
    },
    loginBranding: {
        marginBottom: theme.spacing(2),
        textAlign: 'center'
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(1),
    },
    marginBottomLarge: {
        marginBottom: theme.spacing(3),
    },
    content: {
        paddingBottom: theme.spacing(3)
    },
    actions: {
        borderTop: '1px solid',
        borderColor: theme.palette.divider
    },
    participateButton: {
        margin: theme.spacing(1),
    },
    textSuccess: {
        color: theme.palette.success.main
    }
}));

const screens = [
    'onboard_message',
    'onboard_profile',
    'onboard_password',
    'onboard_membership',
    'onboard_membership_invoice',
    'terms',
    'complete'
];

export default function OnboardLayout(props) {

    const { app, handleApp, hydrateApp } = useApp();

    const [ user, setUser ] = useState({
        id: app.user.id,
        prefix: app.user.prefix || "",
        first: app.user.first,
        middle: app.user.middle || "",
        last: app.user.last,
        suffix: app.user.suffix || "",
        nickname: app.user.nickname || "",
        marital_status: app.user.marital_status || "",
        spouse: app.user.spouse || "",
        birth_date: app.user.birth_date || "",

        email: app.user.email || "",
        phone_home: app.user.phone_home || "",
        phone_work: app.user.phone_work || "",
        phone_mobile: app.user.phone_mobile || "",

        address_one: app.user.address_one || "",
        address_two: app.user.address_two || "",
        city: app.user.city || "",
        state: app.user.state || "",
        zip: app.user.zip || "",

        industry: app.user.industry || "",
        employer: app.user.employer || "",
        position: app.user.position || "",

        work_address_one: app.user.work_address_one || "",
        work_address_two: app.user.work_address_two || "",
        work_city: app.user.work_city || "",
        work_state: app.user.work_state || "",
        work_zip: app.user.work_zip || "",

        clear_onboarding: true
    });

    const [ userUpdated, setUserUpdated ] = useState(false);

    const [ password, setPassword ] = useState("");
    const [ passwordConfirmation, setPasswordConfirmation ] = useState("");

    const [ displayProfile, setDisplayProfile ] = useState(true);

    const [ step, setStep ] = useState(null);
    const [ steps, setSteps ] = useState([]);

    const [ termsChecked, setTermsChecked ] = useState((app.user.agreed_to_terms ? true : false));

    const [ stripePromise, setStripePromise ] = useState(null);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState([]);

    const classes = useStyles();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const api = useApi();

    useEffect(() => {
        const newSteps = screens.filter(screen => {
            if (screen == 'complete') return true;
            if (screen == 'terms') return !app.user.agreed_to_terms;

            const val = app.user.association.meta[screen];

            if (screen == 'onboard_membership_invoice') return (val && val != "" && app.user.membership_status.invoice)

            return (val && val != "");
        });

        setSteps(newSteps);
        setStep(newSteps[0]);

        if (
            app.club &&
            Array.isArray(app.club.meta.payment_methods) &&
            app.club.meta.payment_methods.includes('stripe') &&
            app.club.meta.stripe_account_id
        ) {
            const stripeOptions = {stripeAccount: app.club.meta.stripe_account_id};
            setStripePromise(loadStripe(process.env.MIX_STRIPE_KEY, stripeOptions));

        } else {
            setStripePromise(null);
        }
    }, []);

    /************* SETTERS *****************/

    const handleUserChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setUser({ ...user, [name]: value });
        setUserUpdated(true);
    };

    const handleIndustry = (e, dataSet) => {
        const value = (dataSet !== null) ? dataSet.value : "";

        if (isError('industry')) delete errors.industry;

        setUser({ ...user, industry: value });
        setUserUpdated(true);
    }

    const handlePassword = event => {
        const value = event.target.value;
        if (isError('password')) delete errors.password;
        setPassword(value);
    }

    const handlePasswordConfirmation = event => {
        const value = event.target.value;
        if (isError('password_confirmation')) delete errors.password_confirmation;
        setPasswordConfirmation(value);
    };

    const matchesPattern = pattern => {
        return pattern.test(password)
    }

    const checkPassword = (passwordConfirmation === password);

    const handleParticipate = val => {
        setDisplayProfile(val)
    }

    /************* DIALOG NAVIGATION *****************/

    const handleNext = () => {
        const key = steps.indexOf(step);
        let nextKey = key + 1;

        if (step == 'onboard_message') postClearMessage();
        if (step == 'onboard_profile') postClearProfile();
        if (step == 'onboard_profile' && userUpdated) postProfile();
        if (step == 'onboard_password') postClearPassword();
        if (step == 'onboard_password' && password != "") postPassword();
        if (step == 'onboard_membership') {
            postClearMembershipSettings();
            postMembershipSettings();
        }
        if (step == 'onboard_membership_invoice') {
            postClearMembershipInvoice();
            setLoading(false);
        }
        if (step == 'terms') {
            postTerms();
        }
        if (step == 'complete') {
            hydrateApp();
            return false;
        }

        setStep(steps[nextKey])
    }

    const handleBack = () => {
        const key = steps.indexOf(step);
        let previousKey = key - 1;

        if (previousKey < 0) previousKey = 0;

        setStep(steps[previousKey])
    }

    const isNextDisabled = () => {
        const key = steps.indexOf(step);
        if (steps[key] === 'terms' && !termsChecked) return true;
        return false
    }

    const isBackDisabled = () => {
        if (step < 2) return true;
        return false
    }

    const nextText = () => {
        const key = steps.indexOf(step);
        if (step == 'onboard_membership_invoice') return 'Skip';
        if ((key + 1) === steps.length) return "Finish";
        return "Next";
    }

    const toggleTermsChecked = () => {
        setTermsChecked(!termsChecked);
    }

    /**************** POSTERS ******************/

    const postClearMessage = () => {
        api.get('/profile/onboard/message');
    }

    const postClearProfile = () => {
        api.get('/profile/onboard/profile');
    }

    const postClearPassword = () => {
        api.get('/profile/onboard/password');
    }

    const postClearMembershipSettings = () => {
        api.get('/profile/onboard/membership');
    }

    const postClearMembershipInvoice = () => {
        api.get('/profile/onboard/invoice');
    }

    const postProfile = () => {
        setLoading(true);
        setErrors([]);

        api.post('/profile', user)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                    setStep('onboard_profile');
                }
            }).then(() => setLoading(false));
    }

    const postPassword = () => {
        setLoading(true);
        setErrors([]);

        const data = {
            password: password,
            password_confirmation: passwordConfirmation,
            clear_onboarding: true
        }

        api.post('/profile/security/update-password', data)
            .then(res => {

                setPassword("");
                setPasswordConfirmation("");

            }).catch(error => {

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                    setStep('onboard_password')
                }

            }).then(() => setLoading(false));
    }

    const postMembershipSettings = () => {
        setLoading(true);
        setErrors([]);

        api.post('/profile/association/' + app.club.id + '/settings/participate', {participating: displayProfile})
            .catch(error => {})
            .then(() => setLoading(false));
    }

    const postTerms = () => {
        setLoading(true);
        setErrors([]);

        api.post('/profile/terms', {agreed_to_terms: termsChecked})
            .catch(error => {})
            .then((res) => setLoading(false))
    }

    const isError = field => {
        return (errors[field]);
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getBirthDateMessage = () => {
        if (isError('birth_date')) return errors.birth_date;
        return "Administrative Only. Will not be published in Directory.";
    }

    /**************** CONSTANTS ******************/

    const imageContainer = () => {
        if (app.club && app.club.branding.logo) {
            return (
                <div className={classes.loginBranding}>
                    <img
                        className={classes.loginImage}
                        src={ app.club.branding.logo.full_url }
                        alt={ "Welcome to " + app.club.name}
                    />
                </div>
            )
        } else if (! app.club) {
            return (
                <div className={classes.loginBranding}>
                    <img className={classes.loginImage} src={process.env.MIX_BRANDING} alt="Welcome to Club Apps"/>
                </div>
            )
        }

        return null;
    }

    return (
        <main className={classes.main}>
            <Dialog
                maxWidth="sm"
                fullWidth
                open
                fullScreen={mobile}
                >
                <Loading isLoading={loading}/>
                <DialogContent className={classes.content}>
                    {imageContainer()}
                    <Typography component="h2" variant="h5" align="center" gutterBottom>
                        {app.club.name}
                    </Typography>
                    { step === 'onboard_message' &&
                        <Typography color="textSecondary" align="center">
                            {app.user.association.meta.onboard_message}
                        </Typography>
                    }
                    { step === 'onboard_profile' &&
                        <form noValidate>
                            <Typography align="center" gutterBottom color="textSecondary">
                                Please review and confirm your personal information is up-to-date. Having current information eases stress from our administrators and facilitates fluid communication.
                            </Typography>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" component="h3">
                                        Personal Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        id="input-prefix"
                                        select
                                        label="Prefix"
                                        value={user.prefix}
                                        onChange={handleUserChange('prefix')}
                                        fullWidth
                                        error={isError('prefix')}
                                        helperText={getErrorMessage('prefix')}
                                        variant='outlined'
                                        >
                                        <MenuItem value="">n/a</MenuItem>
                                        {Prefixes.map((prefix, i) => (
                                            <MenuItem key={i} value={prefix}>{prefix}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        id="input-first"
                                        label="First"
                                        value={user.first}
                                        onChange={handleUserChange('first')}
                                        fullWidth
                                        required
                                        error={isError('first')}
                                        helperText={getErrorMessage('first')}
                                        variant='outlined'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-middle"
                                        label="Middle"
                                        value={user.middle}
                                        onChange={handleUserChange('middle')}
                                        fullWidth
                                        error={isError('middle')}
                                        helperText={getErrorMessage('middle')}
                                        variant='outlined'
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-last"
                                        label="Last"
                                        value={user.last}
                                        onChange={handleUserChange('last')}
                                        fullWidth
                                        required
                                        error={isError('last')}
                                        helperText={getErrorMessage('last')}
                                        variant='outlined'
                                        />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        id="input-suffix"
                                        label="Suffix"
                                        value={user.suffix}
                                        onChange={handleUserChange('suffix')}
                                        fullWidth
                                        error={isError('suffix')}
                                        helperText={getErrorMessage('suffix')}
                                        variant='outlined'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        id="input-nickname"
                                        label="Nickname"
                                        value={user.nickname}
                                        onChange={handleUserChange('nickname')}
                                        fullWidth
                                        error={isError('nickname')}
                                        helperText={getErrorMessage('nickname')}
                                        variant='outlined'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-marital-status"
                                        select
                                        label="Marital Status"
                                        value={user.marital_status}
                                        onChange={handleUserChange('marital_status')}
                                        fullWidth
                                        error={isError('marital_status')}
                                        helperText={getErrorMessage('marital_status')}
                                        variant='outlined'
                                        >
                                        <MenuItem value="" >n/a</MenuItem>
                                        <MenuItem value="Married" >Married</MenuItem>
                                        <MenuItem value="Single" >Single</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-spouse"
                                        label="Spouse"
                                        value={user.spouse}
                                        onChange={handleUserChange('spouse')}
                                        fullWidth
                                        error={isError('spouse')}
                                        helperText={getErrorMessage('spouse')}
                                        variant='outlined'
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <NumberFormat
                                        format="##/##/####"
                                        customInput={TextField}
                                        label="Birth Date"
                                        value={user.birth_date}
                                        onChange={handleUserChange('birth_date')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('birth_date')}
                                        helperText={getBirthDateMessage()}
                                        placeholder="mm/dd/yyyy"
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" component="h3">
                                        Contact Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-email"
                                        type="email"
                                        label="Email"
                                        value={user.email}
                                        onChange={handleUserChange('email')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('email')}
                                        helperText={getErrorMessage('email')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <NumberFormat
                                        format="(###) ###-####"
                                        allowEmptyFormatting
                                        customInput={TextField}
                                        id="input-mobile-phone"
                                        type="tel"
                                        label="Mobile Phone"
                                        value={user.phone_mobile}
                                        onChange={handleUserChange('phone_mobile')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('phone_mobile')}
                                        helperText={getErrorMessage('phone_mobile')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <NumberFormat
                                        format="(###) ###-####"
                                        allowEmptyFormatting
                                        customInput={TextField}
                                        id="input-home-phone"
                                        type="tel"
                                        label="Home Phone"
                                        value={user.phone_home}
                                        onChange={handleUserChange('phone_home')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('phone_home')}
                                        helperText={getErrorMessage('phone_home')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <NumberFormat
                                        format="(###) ###-####"
                                        allowEmptyFormatting
                                        customInput={TextField}
                                        id="input-work-phone"
                                        type="tel"
                                        label="Work Phone"
                                        value={user.phone_work}
                                        onChange={handleUserChange('phone_work')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('phone_work')}
                                        helperText={getErrorMessage('phone_work')}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" component="h3">
                                        Home Address
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-address-one"
                                        label="Address One"
                                        value={user.address_one}
                                        onChange={handleUserChange('address_one')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('address_one')}
                                        helperText={getErrorMessage('address_one')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-address-two"
                                        label="Address Two"
                                        value={user.address_two}
                                        onChange={handleUserChange('address_two')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('address_two')}
                                        helperText={getErrorMessage('address_two')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        id="input-city"
                                        label="City"
                                        value={user.city}
                                        onChange={handleUserChange('city')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('city')}
                                        helperText={getErrorMessage('city')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        id="input-state"
                                        select
                                        label="State"
                                        value={user.state}
                                        onChange={handleUserChange('state')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('state')}
                                        helperText={getErrorMessage('state')}
                                        >
                                        {States.map( state => {
                                            return (
                                                <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                            )
                                        })}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        id="input-zip"
                                        label="ZIP"
                                        value={user.zip}
                                        onChange={handleUserChange('zip')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('zip')}
                                        helperText={getErrorMessage('zip')}
                                        />
                                </Grid>
                            </Grid>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" component="h3">
                                        Work Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <IndustryTextField
                                        value={user.industry}
                                        onChange={handleIndustry}
                                        groupBy={'individual_cat'}
                                        error={isError('industry')}
                                        helperText={getErrorMessage('industry')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-employer"
                                        label="Employer"
                                        value={user.employer}
                                        onChange={handleUserChange('employer')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('employer')}
                                        helperText={getErrorMessage('employer')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-position"
                                        label="Position"
                                        value={user.position}
                                        onChange={handleUserChange('position')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('position')}
                                        helperText={getErrorMessage('position')}
                                        />
                                </Grid>
                            </Grid>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" component="h3">
                                        Work Address
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-work-address-one"
                                        label="Work Address One"
                                        value={user.work_address_one}
                                        onChange={handleUserChange('work_address_one')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('work_address_one')}
                                        helperText={getErrorMessage('work_address_one')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-work-address-two"
                                        label="Work Address Two"
                                        value={user.work_address_two}
                                        onChange={handleUserChange('work_address_two')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('work_address_two')}
                                        helperText={getErrorMessage('work_address_two')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        id="input-work-city"
                                        label="Work City"
                                        value={user.work_city}
                                        onChange={handleUserChange('work_city')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('work_city')}
                                        helperText={getErrorMessage('work_city')}
                                        />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        id="input-work-state"
                                        select
                                        label="Work State"
                                        value={user.work_state}
                                        onChange={handleUserChange('work_state')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('work_state')}
                                        helperText={getErrorMessage('work_state')}
                                        >
                                        {States.map( state => {
                                            return (
                                                <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                            )
                                        })}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        id="input-work-zip"
                                        label="Work ZIP"
                                        value={user.work_zip}
                                        onChange={handleUserChange('work_zip')}
                                        fullWidth
                                        variant='outlined'
                                        error={isError('work_zip')}
                                        helperText={getErrorMessage('work_zip')}
                                        />
                                </Grid>
                            </Grid>
                        </form>
                    }
                    { step === 'onboard_password' &&
                        <form noValidate autoComplete="off">
                            <Typography align="center" gutterBottom color="textSecondary">
                                If this is your first time to log in, we highly recommend creating a new password to better secure your account. While all of our data is encrypted, a strong password can better insure your privacy.
                            </Typography>
                            <Grid container spacing={3} className={classes.marginTop}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="input-password"
                                        label="Password"
                                        value={password}
                                        onChange={handlePassword}
                                        variant="outlined"
                                        fullWidth
                                        type="password"
                                        name="password"
                                        margin="normal"
                                        error={isError('password')}
                                        helperText={getErrorMessage('password')}
                                    />
                                    <TextField
                                        id="input-check-password"
                                        label="Verify Password"
                                        value={passwordConfirmation}
                                        onChange={handlePasswordConfirmation}
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        name="password-confirmation"
                                        margin="normal"
                                        error={!checkPassword}
                                        helperText={checkPassword ? "" : "Passwords do not match"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" className={classes.marginBottom}>
                                        New password must:
                                    </Typography>
                                    {PasswordPatterns.map((p, i) => (
                                        <Fragment key={i}>
                                            <Typography
                                                gutterBottom
                                                variant="body2"
                                                color={matchesPattern(p.regex) ? 'primary' : 'error'}
                                                >
                                                {p.label}
                                            </Typography>
                                        </Fragment>
                                    ))}
                                </Grid>
                            </Grid>
                        </form>
                    }
                    { step === 'onboard_membership' &&
                        <form noValidate autoComplete="off">
                            <Typography align="center" gutterBottom color="textSecondary">
                                Part of our service includes the ability to share your profile in the digital member directory in order to allow for better communication between members. We will never share your information with anyone outside of the club without your consent.
                            </Typography>
                            <Typography component="h2" variant="subtitle1" align="center" gutterBottom>
                                Would you like to participate in the digital member directory?
                            </Typography>
                            <Typography component="div" align="center" gutterBottom>
                                <Button
                                    onClick={() => handleParticipate(false)}
                                    color={(!displayProfile) ? "secondary" : "default"}
                                    variant="contained"
                                    className={classes.participateButton}
                                    >
                                    Do Not Participate
                                </Button>
                                <Button onClick={() => handleParticipate(true)}
                                    color={displayProfile ? "secondary" : "default"}
                                    variant="contained"
                                    className={classes.participateButton}
                                    >
                                    Participate
                                </Button>
                            </Typography>
                        </form>
                    }
                    {step === "onboard_membership_invoice" &&
                        <div className={classes.membershipInvoiceContainer}>
                            {app.user.membership_status.in_good_standing ? (
                                <Typography
                                    align="center"
                                    color="textSecondary"
                                    className={classes.marginBottomLarge}
                                    >
                                    Your membership is up for renewal. You can pay below or visit your membership page later to view and pay your membership invoice.
                                </Typography>
                            ) : (
                                <Fragment>
                                    <Typography align="center" gutterBottom variant="h6" color="error">
                                        Your membership payment is Past Due.
                                    </Typography>
                                    <Typography
                                        align="center"
                                        color="textSecondary"
                                        className={classes.marginBottomLarge}
                                        >
                                        Please make a payment below or visit your membership page to pay at a later time. Some services may not be available while your membership is past due.
                                    </Typography>
                                </Fragment>
                            )}
                            {stripePromise &&
                                <Elements stripe={stripePromise}>
                                    <ChargePaymentMethodForm
                                        onCharge={handleNext}
                                        user={app.user}
                                        invoice={app.user.membership_status.invoice}
                                        club={app.club}
                                        setLoading={setLoading}
                                        loading={loading}
                                        urlPrefix={'/profile'}
                                    />
                                </Elements>
                            }
                        </div>
                    }
                    { step === "terms" &&
                        <form noValidate autoComplete="off">
                            <Typography component="h2" variant="h6" align="center" gutterBottom>
                                Terms of Service
                            </Typography>
                            <Typography align="center" gutterBottom color="textSecondary">
                                The <Link color="secondary" href={`${process.env.MIX_LEGAL_BASEURL}${process.env.MIX_LEGAL_ROUTE_USER}`} target="_blank">End User Terms of Service</Link> explains the terms in which Club Apps, our organization's software provider, offers its platform software service to you, our users. Please take a few minutes to read through the terms and if you have any questions please feel free to contact Club Apps at <Link color="secondary" href={"mailto:" + process.env.MIX_CA_SUPPORT_EMAIL} target="_blank">{process.env.MIX_CA_SUPPORT_EMAIL}</Link>, they will try to answer your inquiry as soon as possible.
                            </Typography>
                            <Typography component="h2" variant="subtitle1" align="center" gutterBottom>
                                Check the box to finish your account setup.
                            </Typography>
                            <Typography component="div" variant="body1" align="center" gutterBottom>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={termsChecked}
                                            onChange={toggleTermsChecked}
                                            value="termsChecked"
                                            color="secondary"
                                            />
                                    }
                                    label={
                                        <span>I agree to the <Link color="secondary" href={process.env.MIX_LEGAL_BASEURL + process.env.MIX_LEGAL_ROUTE_USER} target="_blank">Terms of Service</Link></span>
                                    }
                                />
                            </Typography>
                        </form>
                    }
                    { step === 'complete' &&
                        <div className={classes.completeContainer}>
                            <Typography align="center" variant="h5" gutterBottom className={classes.textSuccess}>
                                You're all set!
                            </Typography>
                            <Typography align="center" gutterBottom color="textSecondary">
                                 Press the <strong>FINISH</strong> button to be redirected back to the main app. Thanks for your input, we greatly appreciate your help as we continue to improve our membership platform.
                            </Typography>
                        </div>
                    }
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <Button onClick={handleBack} color="secondary" disabled={isBackDisabled()}>
                        Back
                    </Button>
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        color="secondary"
                        disabled={isNextDisabled()}
                        >
                        {nextText()}
                    </Button>
                </DialogActions>
            </Dialog>
        </main>
    )
};
